/** @jsxImportSource @emotion/react */
import React, { useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import { actions as adminActions } from '@store/slice/admin-slice';
import { useAdminStudentMetricsQuery, useAdminDailyMetricsQuery } from '@hook/admin/useApi';
import { useInfiniteScroll } from '@hook/common/useScroll';
import StudentList from '@component/admin/student/student-home/student-list';
import DailyList from '@component/admin/student/student-home/daily-list';
import OverviewPanel from '@component/admin/student/student-home/overview-panel';

const STUDENT_PER_PAGE = 20;
const DAILY_PER_PAGE = 30;

const Wrapper = styled.div`
  display: flex;
  flex-flow: row nowrap;
  gap: 16px;
  width: 100%;
  height: 100%;
  padding: 24px;
  box-sizing: border-box;
`;

const LeftPane = styled.div`
  display: flex;
  flex-flow: column nowrap;
  height: 100%;
`;

const RightPane = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 16px;
  flex: 1;
  height: 100%;
  min-height: 0;
  overflow: hidden;
`;

const TopPane = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 12px;
  flex: 1 1 60%;
  min-height: 0;
  overflow-y: auto;
`;

const BottomPane = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 12px;
  flex: 0 0 40%;
  min-height: 0;
  overflow-y: auto;
`;

const toDateString = (date: Date): string => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const StudentHome: React.FC = () => {
  const dispatch = useTypedDispatch();
  const selectedDateFromStore = useTypedSelector(
    (state) => state.rootReducer?.adminReducer?.selectedMetricDate,
  );
  const selectedDate = selectedDateFromStore ?? toDateString(new Date());

  const topPaneRef = useRef<HTMLDivElement | null>(null);
  const bottomPaneRef = useRef<HTMLDivElement | null>(null);

  const [studied, setStudied] = useState<boolean>(false);

  const studentQuery = useAdminStudentMetricsQuery({
    date: selectedDate,
    perPage: STUDENT_PER_PAGE,
    studied,
  });

  const dailyQuery = useAdminDailyMetricsQuery({
    perPage: DAILY_PER_PAGE,
  });

  const students = studentQuery.data?.pages.flatMap((p) => p.data ?? []) ?? [];
  const dailies = dailyQuery.data?.pages.flatMap((p) => p.data ?? []) ?? [];

  const studentScroll = useInfiniteScroll({
    hasNextPage: studentQuery.hasNextPage ?? false,
    isFetchingNextPage: studentQuery.isFetchingNextPage ?? false,
    fetchNextPage: studentQuery.fetchNextPage,
    root: topPaneRef,
  });

  const dailyScroll = useInfiniteScroll({
    hasNextPage: dailyQuery.hasNextPage ?? false,
    isFetchingNextPage: dailyQuery.isFetchingNextPage ?? false,
    fetchNextPage: dailyQuery.fetchNextPage,
    root: bottomPaneRef,
  });

  const handleSelectDate = (date: string) => {
    dispatch(adminActions.storeSelectedMetricDate(date));
  };

  return (
    <Wrapper>
      <LeftPane>
        <OverviewPanel />
      </LeftPane>
      <RightPane>
        <TopPane ref={topPaneRef}>
          <StudentList
            selectedDate={selectedDate}
            students={students}
            isLoading={studentQuery.isLoading}
            isFetchingNextPage={studentQuery.isFetchingNextPage ?? false}
            isError={studentQuery.isError}
            studied={studied}
            handleRetry={studentQuery.refetch}
            handleStudiedChange={setStudied}
            sentinelRef={studentScroll.sentinelRef}
          />
        </TopPane>
        <BottomPane ref={bottomPaneRef}>
          <DailyList
            dailies={dailies}
            selectedDate={selectedDate}
            isLoading={dailyQuery.isLoading}
            isFetchingNextPage={dailyQuery.isFetchingNextPage ?? false}
            isError={dailyQuery.isError}
            handleSelect={handleSelectDate}
            handleRetry={dailyQuery.refetch}
            sentinelRef={dailyScroll.sentinelRef}
          />
        </BottomPane>
      </RightPane>
    </Wrapper>
  );
};

export default StudentHome;